import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./CapitalProjects.css";

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const PROJECT_STATUS_TABS = ["All", "Closed", "Work Complete"];
const JOB_STATUS_TABS = ["All", "Complete", "Scheduled", "Unscheduled"];

const emptyData = {
  kpis: { openCapitalProjects: 0, openProjectWorkOrders: 0 },
  projects: [],
  projectJobs: [],
};

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const formatCurrency = (val) => {
  if (val === null || val === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
};

const getProjectStatusTone = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "WORK COMPLETE") return "complete";
  if (s === "CLOSED") return "closed";
  if (s === "IN PROGRESS" || s === "ACTIVE") return "progress";
  if (s === "OPEN") return "open";
  return "neutral";
};

const getJobStatusTone = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "COMPLETE") return "complete";
  if (s === "SCHEDULED") return "progress";
  if (s === "UNSCHEDULED") return "open";
  return "neutral";
};

const parseMDY = (str) => {
  if (!str) return null;
  const parts = str.split("/");
  if (parts.length !== 3) return null;
  const d = new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseYMD = (str) => {
  if (!str) return null;
  const d = new Date(str + "T00:00:00");
  return Number.isNaN(d.getTime()) ? null : d;
};

const matchesDateRange = (dateStr, fromDate, toDate) => {
  if (!fromDate && !toDate) return true;
  const date = parseMDY(dateStr);
  if (!date) return !fromDate && !toDate;
  if (fromDate && date < fromDate) return false;
  if (toDate && date > toDate) return false;
  return true;
};

const matchesProjectSearch = (row, term) => {
  if (!term) return true;
  const lower = term.toLowerCase();
  return [
    row.projectNumber,
    row.projectName,
    row.description,
    row.status,
    row.endDate,
    row.poNumber,
    row.property,
  ]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(lower));
};

const matchesJobSearch = (row, term) => {
  if (!term) return true;
  const lower = term.toLowerCase();
  return [
    row.workOrder,
    row.description,
    row.type,
    row.status,
    row.completedDate,
    row.poNumber,
    row.property,
  ]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(lower));
};

/* -------------------------------------------------------------------------- */
/*                              Reusable UI pieces                            */
/* -------------------------------------------------------------------------- */

const KpiCard = ({ label, value, accent, isLoading }) => (
  <div className={`cp-kpi-card ${accent ? `cp-kpi-card--${accent}` : ""}`}>
    <div className="cp-kpi-card__label">{label}</div>
    <div className="cp-kpi-card__value">
      {isLoading ? <span className="card-spinner" /> : value}
    </div>
  </div>
);

const SkeletonRows = ({ cols, count = 6 }) =>
  Array.from({ length: count }).map((_, i) => (
    <tr key={i}>
      {Array.from({ length: cols }).map((_, j) => (
        <td key={j}><span className="skeleton-line" /></td>
      ))}
    </tr>
  ));

const CompanySelect = ({ customers, selectedCustomerId, setSelectedCustomerId, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);
  const selected = customers.find((c) => String(c.id) === String(selectedCustomerId));
  const buttonLabel = selected ? selected.name : "All Companies";
  return (
    <div ref={wrapperRef} className="cp-filter-field">
      <label className="cp-filter-field__label">Company Selection</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className="cp-select-btn"
      >
        <span className="cp-select-btn__text">{buttonLabel}</span>
        <span className={`cp-select-btn__chevron ${open ? "is-open" : ""}`}>▾</span>
      </button>
      {open && !disabled ? (
        <div className="cp-multiselect-menu">
          <div className="cp-multiselect-list">
            <div
              className={`cp-checkbox-row${!selectedCustomerId ? " is-selected" : ""}`}
              onClick={() => { setSelectedCustomerId(""); setOpen(false); }}
            >
              <span>All Companies</span>
            </div>
            {customers.map((c) => (
              <div
                key={c.id}
                className={`cp-checkbox-row${String(selectedCustomerId) === String(c.id) ? " is-selected" : ""}`}
                onClick={() => { setSelectedCustomerId(String(c.id)); setOpen(false); }}
              >
                <span>{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PropertyMultiSelect = ({ properties, selectedPropertyIds, setSelectedPropertyIds, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const selectedNames = properties
    .filter((p) => selectedPropertyIds.includes(String(p.id)))
    .map((p) => p.name);

  const buttonLabel = (() => {
    if (!selectedPropertyIds.length) return "Select values";
    if (selectedPropertyIds.length === 1) return selectedNames[0] || "1 Property";
    return `${selectedPropertyIds.length} selected`;
  })();

  const toggle = (id) => {
    setSelectedPropertyIds((curr) => {
      const s = String(id);
      return curr.includes(s) ? curr.filter((x) => x !== s) : [...curr, s];
    });
  };

  return (
    <div ref={wrapperRef} className="cp-filter-field">
      <label className="cp-filter-field__label">Property Selection</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className="cp-select-btn"
      >
        <span className="cp-select-btn__text">{buttonLabel}</span>
        <span className={`cp-select-btn__chevron ${open ? "is-open" : ""}`}>▾</span>
      </button>

      {open && !disabled ? (
        <div className="cp-multiselect-menu">
          <div className="cp-multiselect-actions">
            <button type="button" className="cp-pill-btn" onClick={() => setSelectedPropertyIds([])}>All</button>
            <button type="button" className="cp-pill-btn" onClick={() => setSelectedPropertyIds(properties.map((p) => String(p.id)))}>Select All</button>
          </div>
          <div className="cp-multiselect-list">
            {properties.length ? (
              properties.map((p) => (
                <label key={p.id} className="cp-checkbox-row">
                  <input type="checkbox" checked={selectedPropertyIds.includes(String(p.id))} onChange={() => toggle(p.id)} />
                  <span>{p.name}</span>
                </label>
              ))
            ) : (
              <div className="cp-multiselect-empty">No properties available.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const DateRangeFilter = ({ label, fromDate, toDate, setFromDate, setToDate }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const hasValue = fromDate || toDate;
  const displayLabel = (() => {
    if (fromDate && toDate) return `${fromDate} – ${toDate}`;
    if (fromDate) return `From ${fromDate}`;
    if (toDate) return `To ${toDate}`;
    return "Select date range";
  })();

  return (
    <div ref={wrapperRef} className="cp-filter-field">
      <label className="cp-filter-field__label">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`cp-select-btn ${hasValue ? "has-value" : ""}`}
      >
        <span className="cp-date-icon">&#128197;</span>
        <span className="cp-select-btn__text">{displayLabel}</span>
        <span className={`cp-select-btn__chevron ${open ? "is-open" : ""}`}>▾</span>
      </button>

      {open ? (
        <div className="cp-date-menu">
          <div className="cp-date-row">
            <label className="cp-date-label">From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="cp-date-input" />
          </div>
          <div className="cp-date-row">
            <label className="cp-date-label">To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="cp-date-input" />
          </div>
          {hasValue ? (
            <button type="button" className="cp-pill-btn" onClick={() => { setFromDate(""); setToDate(""); }}>Clear</button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                               CapitalProjects                              */
/* -------------------------------------------------------------------------- */

const CapitalProjects = ({ viewerEmail }) => {
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);

  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);

  const [filterError, setFilterError] = useState("");
  const [dataError, setDataError] = useState("");

  const [data, setData] = useState(emptyData);

  // Project table state
  const [activeProjectStatusTab, setActiveProjectStatusTab] = useState("All");
  const [projectDateFrom, setProjectDateFrom] = useState("");
  const [projectDateTo, setProjectDateTo] = useState("");

  // Project jobs table state
  const [activeJobStatusTab, setActiveJobStatusTab] = useState("All");
  const [activeJobTypeTab, setActiveJobTypeTab] = useState("All");
  const [jobDateFrom, setJobDateFrom] = useState("");
  const [jobDateTo, setJobDateTo] = useState("");

  // Shared search
  const [searchTerm, setSearchTerm] = useState("");

  // Load filters
  useEffect(() => {
    const load = async () => {
      try {
        setFiltersLoading(true);
        setFilterError("");
        const token = localStorage.getItem("token");
        if (!token) { setFilterError("No auth token found."); return; }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/capital-projects/capital-projects-filters`,
          { headers: { Authorization: `Bearer ${token}` }, params: viewerEmail ? { email: viewerEmail } : undefined }
        );
        const d = res?.data || {};
        setCustomers(Array.isArray(d.customers) ? d.customers : []);
        setProperties(Array.isArray(d.properties) ? d.properties : []);
      } catch (err) {
        console.error("Failed to load capital projects filters:", err);
        setFilterError("Failed to load filters.");
      } finally {
        setFiltersLoading(false);
      }
    };
    load();
  }, [viewerEmail]);

  const filteredProperties = useMemo(() => {
    if (!selectedCustomerId) return properties;
    return properties.filter((p) => String(p.customerId ?? "") === String(selectedCustomerId));
  }, [properties, selectedCustomerId]);

  useEffect(() => {
    const allowed = new Set(filteredProperties.map((p) => String(p.id)));
    setSelectedPropertyIds((curr) => curr.filter((id) => allowed.has(id)));
  }, [filteredProperties]);

  // Load data
  useEffect(() => {
    const load = async () => {
      if (filtersLoading || filterError) return;
      try {
        setDataLoading(true);
        setDataError("");
        const token = localStorage.getItem("token");
        if (!token) { setDataError("No auth token found."); return; }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/capital-projects/capital-projects-data`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              ...(viewerEmail ? { email: viewerEmail } : {}),
              ...(selectedCustomerId ? { customerId: selectedCustomerId } : {}),
              ...(selectedPropertyIds.length ? { propertyIds: selectedPropertyIds.join(",") } : {}),
            },
          }
        );
        setData(res?.data || emptyData);
      } catch (err) {
        console.error("Failed to load capital projects data:", err);
        setDataError("Failed to load capital projects data.");
        setData(emptyData);
      } finally {
        setDataLoading(false);
      }
    };
    load();
  }, [filtersLoading, filterError, viewerEmail, selectedCustomerId, selectedPropertyIds]);

  // Derived job type options
  const jobTypeOptions = useMemo(() => {
    const types = new Set();
    for (const row of data.projectJobs || []) {
      if (row.type) types.add(row.type);
    }
    return ["All", ...Array.from(types).sort()];
  }, [data.projectJobs]);

  const projectDateFromObj = useMemo(() => parseYMD(projectDateFrom), [projectDateFrom]);
  const projectDateToObj = useMemo(() => parseYMD(projectDateTo), [projectDateTo]);
  const jobDateFromObj = useMemo(() => parseYMD(jobDateFrom), [jobDateFrom]);
  const jobDateToObj = useMemo(() => parseYMD(jobDateTo), [jobDateTo]);

  const visibleProjects = useMemo(() => {
    let rows = data.projects || [];

    if (activeProjectStatusTab === "Closed") {
      rows = rows.filter((r) => String(r.status || "").toUpperCase() === "CLOSED");
    } else if (activeProjectStatusTab === "Work Complete") {
      rows = rows.filter((r) => String(r.status || "").toUpperCase() === "WORK COMPLETE");
    }

    rows = rows.filter((r) => matchesDateRange(r.endDate, projectDateFromObj, projectDateToObj));
    rows = rows.filter((r) => matchesProjectSearch(r, searchTerm));
    return rows;
  }, [data.projects, activeProjectStatusTab, projectDateFromObj, projectDateToObj, searchTerm]);

  const visibleJobs = useMemo(() => {
    let rows = data.projectJobs || [];

    if (activeJobStatusTab !== "All") {
      rows = rows.filter(
        (r) => String(r.status || "").toUpperCase() === activeJobStatusTab.toUpperCase()
      );
    }

    if (activeJobTypeTab !== "All") {
      rows = rows.filter((r) => r.type === activeJobTypeTab);
    }

    rows = rows.filter((r) => matchesDateRange(r.completedDate, jobDateFromObj, jobDateToObj));
    rows = rows.filter((r) => matchesJobSearch(r, searchTerm));
    return rows;
  }, [data.projectJobs, activeJobStatusTab, activeJobTypeTab, jobDateFromObj, jobDateToObj, searchTerm]);

  const handleClearSearch = () => {
    setSelectedCustomerId("");
    setSelectedPropertyIds([]);
    setActiveProjectStatusTab("All");
    setActiveJobStatusTab("All");
    setActiveJobTypeTab("All");
    setSearchTerm("");
    setProjectDateFrom("");
    setProjectDateTo("");
    setJobDateFrom("");
    setJobDateTo("");
  };

  const isLoading = filtersLoading || dataLoading;

  return (
    <div className="cp-page">
      <div className="cp-shell">

        {/* ---- Filter toolbar ---- */}
        <section className="cp-toolbar">
          <div className="cp-toolbar__grid">
            <CompanySelect
              customers={customers}
              selectedCustomerId={selectedCustomerId}
              setSelectedCustomerId={setSelectedCustomerId}
              disabled={filtersLoading || !!filterError}
            />

            <PropertyMultiSelect
              properties={filteredProperties}
              selectedPropertyIds={selectedPropertyIds}
              setSelectedPropertyIds={setSelectedPropertyIds}
              disabled={filtersLoading || !!filterError}
            />

            <DateRangeFilter
              label="Date Completed Filter"
              fromDate={projectDateFrom}
              toDate={projectDateTo}
              setFromDate={setProjectDateFrom}
              setToDate={setProjectDateTo}
            />

            <div className="cp-filter-field">
              <label className="cp-filter-field__label">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="cp-search-input"
                placeholder=""
              />
            </div>

            <div className="cp-toolbar__clear">
              <button type="button" onClick={handleClearSearch} className="cp-clear-btn">
                Clear Search
              </button>
            </div>
          </div>
        </section>

        {/* ---- Feedback ---- */}
        {filterError ? <div className="cp-feedback cp-feedback--error">{filterError}</div> : null}
        {dataError ? <div className="cp-feedback cp-feedback--error">{dataError}</div> : null}

        {!filterError ? (
          <>
            {/* ---- KPI cards ---- */}
            <div className="cp-kpi-row">
              <KpiCard
                label="Open Capital Projects"
                value={data.kpis?.openCapitalProjects ?? 0}
                accent="open"
                isLoading={isLoading}
              />
              <KpiCard
                label="Open Project Work Orders"
                value={data.kpis?.openProjectWorkOrders ?? 0}
                accent="secondary"
                isLoading={isLoading}
              />
            </div>

            {/* ---- Projects table ---- */}
            <section className="cp-table-card">
              <div className="cp-tab-row">
                <div className="cp-tab-group">
                  <span className="cp-tab-group__label">Project Status</span>
                  <div className="cp-tabs">
                    {PROJECT_STATUS_TABS.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveProjectStatusTab(tab)}
                        className={`cp-tab ${activeProjectStatusTab === tab ? "is-active" : ""}`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="cp-table-wrap">
                {isLoading || visibleProjects.length > 0 ? (
                  <table className="cp-table">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Project Name</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>End Date</th>
                        <th>PO Number</th>
                        <th>Contract Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? <SkeletonRows cols={7} /> : visibleProjects.map((row, idx) => (
                        <tr key={row.projectId || `proj-${idx}`} className={idx % 2 === 0 ? "" : "is-alt"}>
                          <td><div className="cp-primary-cell">{row.projectNumber || "—"}</div></td>
                          <td className="cp-name-cell">{row.projectName || "—"}</td>
                          <td className="cp-description-cell">{row.description || "—"}</td>
                          <td>
                            <span className={`cp-status-pill cp-status-pill--${getProjectStatusTone(row.status)}`}>
                              {row.status || "—"}
                            </span>
                          </td>
                          <td>{row.endDate || "—"}</td>
                          <td>{row.poNumber || "—"}</td>
                          <td className="cp-amount-cell">{formatCurrency(row.contractAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="cp-empty-state">No capital projects found</div>
                )}
              </div>
            </section>

            {/* ---- Project Work Orders table ---- */}
            <section className="cp-table-card cp-table-card--jobs">
              <div className="cp-tab-row">
                <div className="cp-tab-group">
                  <span className="cp-tab-group__label">Project Work Order Status</span>
                  <div className="cp-tabs">
                    {JOB_STATUS_TABS.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveJobStatusTab(tab)}
                        className={`cp-tab ${activeJobStatusTab === tab ? "is-active" : ""}`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="cp-tab-group">
                  <span className="cp-tab-group__label">Project Work Order Type</span>
                  <div className="cp-tabs">
                    {jobTypeOptions.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveJobTypeTab(tab)}
                        className={`cp-tab ${activeJobTypeTab === tab ? "is-active" : ""}`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="cp-table-wrap">
                {isLoading || visibleJobs.length > 0 ? (
                  <table className="cp-table">
                    <thead>
                      <tr>
                        <th>Work Order</th>
                        <th>Description</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Completed Date</th>
                        <th>PO Number</th>
                        <th>Billed Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? <SkeletonRows cols={7} /> : visibleJobs.map((row, idx) => (
                        <tr key={row.jobId || `job-${idx}`} className={idx % 2 === 0 ? "" : "is-alt"}>
                          <td><div className="cp-primary-cell">{!row.workOrder ? "—" : row.reportUrl ? (<a href={row.reportUrl} target="_blank" rel="noopener noreferrer" className="cp-primary-link">{row.workOrder}</a>) : row.workOrder}</div></td>
                          <td className="cp-description-cell">{row.description || "—"}</td>
                          <td>{row.type || "—"}</td>
                          <td>
                            <span className={`cp-status-pill cp-status-pill--${getJobStatusTone(row.status)}`}>
                              {row.status || "—"}
                            </span>
                          </td>
                          <td>{row.completedDate || "—"}</td>
                          <td>{row.poNumber || "—"}</td>
                          <td className="cp-amount-cell">{formatCurrency(row.billedAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="cp-empty-state">No project work orders found</div>
                )}
              </div>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default CapitalProjects;
